import {
  AHEmptyScreen,
  AHSeparator,
  AHText,
  Box,
  ListItem,
  TodoPrioIcon,
} from "arkh";
import React, {
  Suspense,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from "react";
import { FlatList } from "react-native-gesture-handler";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { WorkspaceSignalStackScreenProps } from "../WorkspaceSignalStack";
import { WorkspaceSignalsArchivedQuery } from "./__generated__/WorkspaceSignalsArchivedQuery.graphql";
import { WorkspaceSignalsArchivedList_archiveSignals$key } from "./__generated__/WorkspaceSignalsArchivedList_archiveSignals.graphql";
import { useOrgHomeDetailLayout } from "../../useOrgHomeDetailContainerStyle";
import { ColumnTitle } from "../../SortableColumnTitle";

import { WorkspaceSignalListItemDescription } from "../WorkspaceSignalListItemDescription";
import { WorkspaceSignalPrioColumnContainer } from "../WorkspaceSignalItemRowContainer";
import { OrgSearchInput } from "../../OrgSearchInput";
import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { LoadingList } from "../../WorkspaceFiles/LoadingList";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { Image } from "expo-image";
import { useWorkspaceId } from "../../workspaceId";
import { FormattedDateInWorkspace } from "../../../../common/dates/FormattedDate";
import { WorkspaceDetailNavigationHeader } from "../../WorkspaceDetailNavigationHeader";
import { HeaderButton } from "@/src/components";
import { useWorkspaceSignalsHeaderBackTitle } from "./useWorkspaceSignalsHeaderBackTitle";

export function WorkspaceSignalsArchived(
  props: WorkspaceSignalStackScreenProps<"WorkspaceSignalsArchived">
) {
  const openSignal = useCallback(
    (signalWorkspaceId: string) => {
      props.navigation.navigateDeprecated("WorkspaceSignalDetail", {
        signalWorkspaceId,
        workspaceId: props.route.params.workspaceId,
      });
    },
    [props.navigation, props.route.params.workspaceId]
  );
  const headerBackTitle = useWorkspaceSignalsHeaderBackTitle();
  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <HeaderButton
            type="WEB-BACK"
            customLabel={headerBackTitle}
            onPress={() => props.navigation.goBack()}
          />
        }
        right={null}
      />
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <WorkspaceSignalsArchivedComponent
            fetchKey={fetchKey}
            openSignal={openSignal}
          />
        )}
      </LazyQueryNetworkBoundrary>
    </>
  );
}

function WorkspaceArchivedSignalsTitle() {
  return (
    <FormattedMessage
      defaultMessage="Archived Signals"
      description="WorkspaceSignalsArchived header title"
    />
  );
}

function WorkspaceSignalsArchivedComponent(props: {
  fetchKey: string;

  openSignal: (signalWorkspaceId: string) => void;
}) {
  const [search, setSearch] = useState("");

  const query = useDeferredValue(search);

  const workspaceId = useWorkspaceId();
  const data = useLazyLoadQuery<WorkspaceSignalsArchivedQuery>(
    graphql`
      query WorkspaceSignalsArchivedQuery(
        $workspaceId: ID!
        $after: String
        $first: Int
        $query: String
      ) {
        workspace(id: $workspaceId) {
          id
          ...WorkspaceSignalsArchivedList_archiveSignals
        }
      }
    `,
    {
      workspaceId,
      query,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const header = useMemo(() => {
    return (
      <Box color="surface-background-light" spaceTop="medium">
        <OrgSearchInput value={search} onChangeText={setSearch} />
        <WorkspaceSignalsArchivedListItemHeader />
      </Box>
    );
  }, [search]);

  return (
    <Suspense fallback={<LoadingList header={header} />}>
      <WorkspaceSignalsArchivedList
        workspace={data.workspace}
        openSignal={props.openSignal}
        header={header}
      />
    </Suspense>
  );
}

function WorkspaceSignalsArchivedList(props: {
  workspace: WorkspaceSignalsArchivedList_archiveSignals$key;
  header: React.ReactElement;
  openSignal: (signalWorkspaceId: string) => void;
}) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment WorkspaceSignalsArchivedList_archiveSignals on Workspace
      @refetchable(
        queryName: "WorkspaceSignalsArchivedList_WorkspaceRefetchQuery"
      ) {
        id
        hasArchivedSignal
        archivedSignals(first: $first, after: $after, query: $query)
          @connection(
            key: "WorkspaceSignalsArchivedList_archiveSignals_archivedSignals"
          ) {
          edges {
            node {
              id
              publishedAtDate {
                ...FormattedDate
              }

              priority
              archivedAtDate {
                ...FormattedDate
              }

              ...WorkspaceSignalListItemDescription_signal
            }
          }
        }
      }
    `,
    props.workspace
  );

  const listData = useMemo(() => {
    return data.archivedSignals.edges.filter((e) => e.node?.id);
  }, [data.archivedSignals.edges]);

  const detailLayout = useOrgHomeDetailLayout();

  const onReachEnd = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(30);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  if (!data.hasArchivedSignal) {
    return (
      <AHEmptyScreen
        title={<WorkspaceArchivedSignalsTitle />}
        // TODO: Intl
        subtitle="Det finns inga arkiverade aktiviteter. När du arkiverar en aktivitet hamnar den här."
        illustration={
          <Box style={{ flex: 1, minWidth: 500 }}>
            <Image
              contentFit="contain"
              contentPosition="top center"
              style={{ flex: 1 }}
              source={require("./workspace_signal_archive_empty.png")}
            />
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./workspace_signal_archive_empty.png")}
            />
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./workspace_signal_archive_empty.png")}
            />
          </Box>
        }
      />
    );
  }

  return (
    <FlatList
      ListHeaderComponent={props.header}
      onEndReached={onReachEnd}
      contentContainerStyle={detailLayout.contentContainerStyle}
      data={listData}
      renderItem={({ item, index }) => (
        <ListItem
          isLast={index + 1 === listData.length}
          onPress={() => {
            props.openSignal(item.node.id);
          }}
          border="border-primary"
        >
          <WorkspaceSignalsArchivedListItemContainer
            archivedAt={
              <AHText numberOfLines={1}>
                {item.node.archivedAtDate ? (
                  <FormattedDateInWorkspace date={item.node.archivedAtDate} />
                ) : null}
              </AHText>
            }
            priority={<TodoPrioIcon priority={item.node.priority} />}
            pulishedAt={
              <AHText numberOfLines={1}>
                {item.node.publishedAtDate ? (
                  <FormattedDateInWorkspace date={item.node.publishedAtDate} />
                ) : null}
              </AHText>
            }
            description={
              <WorkspaceSignalListItemDescription workspaceSignal={item.node} />
            }
          />
        </ListItem>
      )}
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
      ListEmptyComponent={WorkspaceEmptyListItem}
      keyExtractor={(item) => item.node.id}
    />
  );
}

function WorkspaceSignalsArchivedListItemContainer(props: {
  priority: React.ReactElement;
  description: React.ReactElement;
  pulishedAt: React.ReactElement;
  archivedAt: React.ReactElement;
}) {
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      <WorkspaceSignalPrioColumnContainer>
        {props.priority}
      </WorkspaceSignalPrioColumnContainer>
      <Box style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
        <Box style={{ width: "40%" }} spaceRight="medium">
          {props.description}
        </Box>
        <Box style={{ width: "20%" }} spaceRight="medium">
          {props.pulishedAt}
        </Box>
        <Box style={{ width: "20%" }}>{props.archivedAt}</Box>
      </Box>
    </Box>
  );
}

function WorkspaceSignalsArchivedListItemHeader() {
  const intl_tokens = useIntlTokens();
  return (
    <Box color="surface-background-light">
      <ListItem disabled border="border-primary">
        <WorkspaceSignalsArchivedListItemContainer
          priority={<ColumnTitle>{intl_tokens.Prio}</ColumnTitle>}
          description={<ColumnTitle>{intl_tokens.Description}</ColumnTitle>}
          pulishedAt={<ColumnTitle>{intl_tokens.Published}</ColumnTitle>}
          archivedAt={<ColumnTitle>{intl_tokens.Archived}</ColumnTitle>}
        />
      </ListItem>
      <AHSeparator />
    </Box>
  );
}
